/**
 * Pure helpers for the workspace's "this line has no price yet" state
 * (design.md V16 — Rate cards, phase 2b). No commercial arithmetic beyond a
 * single unit conversion (managed's percent <-> fraction) — everything else
 * is lookup + formatting over data already on the proposal.
 */
import type { PricingModel, Proposal } from '@shared/types';
// A real relative path, not the `@shared/*` alias: this value import needs to
// resolve under plain Node/Vite module resolution too, since test/web runs
// this file directly through root vitest (no alias configured there — see
// tsconfig.json's note on test/web/**). The alias and this path name the
// same file (web/tsconfig.json's `@shared/*` -> `../src/domain/*`).
import { priceUnitLabel } from '../../../src/domain/pricing-model.js';
import { humanizeId } from './format';

export interface MissingPriceItem {
  productId: string;
  /** Catalog display name, falling back to a humanized id when the pinned
   * rate-card snapshot doesn't carry this product (shouldn't happen — a
   * MISSING_PRICE warning only exists for a product on the proposal's own
   * snapshot — but never crash over it). */
  name: string;
  /** priceUnitLabel(product.pricing.model), e.g. "CPM", "% of media spend". '' when the model is unknown. */
  unit: string;
}

/**
 * One entry per MISSING_PRICE warning on the proposal, in the order the
 * validation engine emitted them (one per unpriced plan line). Reads the
 * proposal's own pinned rate-card snapshot for the display name + unit —
 * never the raw process catalog, so a Pamal proposal shows Pamal's names.
 */
export function missingPrices(proposal: Proposal): MissingPriceItem[] {
  const products = proposal.rateCard?.catalog.products ?? [];
  const byId = new Map(products.map((product) => [product.productId, product]));
  const items: MissingPriceItem[] = [];
  for (const issue of proposal.validation.warnings) {
    if (issue.code !== 'MISSING_PRICE' || !issue.productId) continue;
    const product = byId.get(issue.productId);
    items.push({
      productId: issue.productId,
      name: product?.name ?? humanizeId(issue.productId),
      unit: product ? priceUnitLabel(product.pricing.model) : '',
    });
  }
  return items;
}

/** "1 item is missing a price" / "2 items are missing a price". */
export function formatMissingPriceNote(items: MissingPriceItem[]): string {
  const count = items.length;
  return count === 1 ? '1 item is missing a price' : `${count} items are missing a price`;
}

/** "Twitch (CPM)" — the note's per-item label; no unit suffix when unknown. */
export function formatMissingPriceItem(item: MissingPriceItem): string {
  return item.unit ? `${item.name} (${item.unit})` : item.name;
}

/**
 * The inline price field's label per model — distinct from priceUnitLabel
 * (which names the note's parenthetical, e.g. "CPM"): this is what a rep
 * sees next to the input while typing a rate in, e.g. "$ per 1,000".
 */
export function priceInputUnitLabel(model: PricingModel): string {
  switch (model) {
    case 'cpm':
      return '$ per 1,000';
    case 'cpv':
      return '$ per view';
    case 'cpc':
      return '$ per click';
    case 'flat':
      return '$ / month';
    case 'managed':
      return '% of media spend';
    case 'scoped':
      return '$ / month';
    default: {
      const never: never = model;
      throw new Error(`Unknown pricing model: ${String(never)}`);
    }
  }
}

/** 35 -> 0.35. Managed's retail is stored as a fraction of media spend. */
export function percentToFraction(percent: number): number {
  return percent / 100;
}

/** 0.35 -> 35. The inverse of percentToFraction, for displaying a stored override back to a rep. */
export function fractionToPercent(fraction: number): number {
  return fraction * 100;
}

/**
 * The `retail` value to send in a set_price_override mutation for a raw
 * number a rep typed into the inline field — a percent for managed models,
 * the rate itself for everything else (applyPriceOverride's own contract).
 */
export function priceOverrideRetail(model: PricingModel, rawValue: number): number {
  return model === 'managed' ? percentToFraction(rawValue) : rawValue;
}

/**
 * Human change-log label for a price override, e.g. "Priced Twitch at $65
 * CPM" or "Priced TikTok at 35% of media spend" — built from the RAW value a
 * rep typed (before priceOverrideRetail's percent conversion), so the label
 * always reads in the units the rep entered.
 */
export function priceOverrideChangeLabel(model: PricingModel, rawValue: number, productLabel: string): string {
  switch (model) {
    case 'cpm':
      return `Priced ${productLabel} at $${rawValue} CPM`;
    case 'cpv':
      return `Priced ${productLabel} at $${rawValue} per view`;
    case 'cpc':
      return `Priced ${productLabel} at $${rawValue} per click`;
    case 'flat':
    case 'scoped':
      return `Priced ${productLabel} at $${rawValue}/month`;
    case 'managed':
      return `Priced ${productLabel} at ${rawValue}% of media spend`;
    default: {
      const never: never = model;
      throw new Error(`Unknown pricing model: ${String(never)}`);
    }
  }
}
